/*
 * VENDORED from the nn-favorites app (extensions/list-blocks/assets/toggle-button.js).
 * Wrapped in a guarded IIFE so it can coexist with the app's own blocks/embeds if
 * they are enabled later. Do not hand-edit — re-sync from the app when it updates.
 * Loaded by sections/product-hotspots.liquid for the shoppable-hotspot favorites toggle.
 */
(function () {
// @ts-check
/// <reference path="./state-manager.js" />

const LIST_TOGGLE_ANIMATION_DURATION = 2000;
// Matches the .nn-list-card--removing transition in toggle-button.css.
const LIST_CARD_REMOVE_TRANSITION = 300;

/**
 * Serializes list mutations across every toggle button on the page. Rapid
 * toggles used to fire concurrent requests that burst Shopify's GraphQL rate
 * limit; a throttled membership read then flipped the wrong way, so removals
 * "did nothing" after the first few. Running the network commits one at a time
 * keeps each request within budget. The optimistic UI still updates instantly —
 * only the request is queued.
 * @type {Promise<unknown>}
 */
let listMutationChain = Promise.resolve();

/**
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T>}
 */
function enqueueListMutation(task) {
  const run = listMutationChain.then(task, task);
  // Keep the chain alive regardless of any single task's outcome.
  listMutationChain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/**
 * @typedef {Object} ToggleResponse
 * @property {boolean} [success]
 * @property {"added"|"removed"} [action]
 * @property {boolean} [inList]
 * @property {Collection} [collection]
 * @property {string} [error]
 */

/**
 * List toggle component - enhances server-rendered button with interactivity.
 */
class NnListToggleComponent extends HTMLElement {
  #ac = new AbortController();
  #isToggling = false;

  get #inList() {
    const state = window.NnFavoritesState;
    // Until the state manager has any record of this list type (e.g. seeded by
    // a header icon, or mutated by a prior toggle), trust the server-rendered
    // initial state. Otherwise the button would flip to "not in list" on pages
    // where no header icon seeds the list — including the list's own collection
    // page, where every product is in the list.
    if (!state || !state.hasList(this.dataset.listType)) {
      return this.dataset.inList === "true";
    }
    return state.getProducts(this.dataset.listType).has(this.dataset.productId);
  }

  connectedCallback() {
    if (this.dataset.loggedIn !== "true") return;

    const { signal } = this.#ac;

    /** @type {HTMLButtonElement|null} */
    const toggleButton = this.querySelector('[ref="toggleButton"]');
    if (!toggleButton) {
      console.error("[NnListToggle] Toggle button not found");
      return;
    }

    toggleButton.addEventListener("click", (e) => this.#handleClick(e), {
      signal,
    });

    if (window.NnFavoritesState) {
      this.#subscribeToState(signal);
    } else {
      document.addEventListener(
        "nn-favorites:ready",
        () => this.#subscribeToState(signal),
        { once: true, signal },
      );
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /** @param {Event} e */
  async #handleClick(e) {
    e.preventDefault();

    /** @type {HTMLButtonElement|null} */
    const toggleButton = this.querySelector('[ref="toggleButton"]');
    if (!toggleButton || toggleButton.disabled) return;

    const { productId, listType } = this.dataset;
    this.#isToggling = true;
    const newState = !this.#inList;

    // Apply the optimistic UI immediately — this must not wait in the request
    // queue, or rapid toggles would feel unresponsive.
    window.NnFavoritesState?.toggle(listType, productId, newState);
    this.#updateUI();

    // On the list's own collection page, a removed product no longer belongs
    // here, so collapse its card. Kept reversible: restored if the request fails.
    const card = !newState ? this.#listPageCard() : null;
    card?.classList.add("nn-list-card--removing");

    // Animate toggle
    toggleButton.classList.remove("lt-confirmed-add", "lt-confirmed-remove");
    const confirmClass = newState ? "lt-confirmed-add" : "lt-confirmed-remove";
    toggleButton.classList.add(confirmClass);
    setTimeout(
      () => setTimeout(() => toggleButton.classList.remove(confirmClass), 10),
      LIST_TOGGLE_ANIMATION_DURATION,
    );

    // Serialize the network commit so concurrent toggles don't burst the rate
    // limit (see enqueueListMutation).
    try {
      await enqueueListMutation(() => this.#commit(productId, listType, newState, card));
    } finally {
      this.#isToggling = false;
    }
  }

  /**
   * Persists the toggle to the server and reconciles UI with the response.
   * Sends the explicit desired state (`inList`) so the server sets membership
   * idempotently instead of flipping whatever it reads.
   * @param {string} productId
   * @param {string} listType
   * @param {boolean} newState
   * @param {HTMLElement|null} card
   */
  async #commit(productId, listType, newState, card) {
    try {
      const response = await fetch("/apps/nn_favorites/toggle", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId, listType, inList: newState }),
      });

      /** @type {ToggleResponse} */
      const responseData = await response.json();
      if (!response.ok)
        throw new Error(responseData.error || "Failed to update list");

      // Compare against the state we asked for, not the current one: the server
      // echoes the desired state, and intervening toggles may already have moved
      // `#inList` on. Only correct if the server couldn't honor the request.
      if (responseData.inList !== newState) {
        window.NnFavoritesState?.toggle(
          listType,
          productId,
          responseData.inList,
          responseData.collection,
        );
      } else if (responseData.collection) {
        window.NnFavoritesState?.updateCollection(
          listType,
          responseData.collection,
        );
      }
      this.#updateUI();

      // Confirmed removal on the list page: collapse the card out of the grid
      // once the fade-out has played. (If the server says it's still in the
      // list, undo the optimistic collapse.)
      if (card) {
        if (responseData.inList) {
          card.classList.remove("nn-list-card--removing");
        } else {
          setTimeout(
            () => card.classList.add("nn-list-card--removed"),
            LIST_CARD_REMOVE_TRANSITION,
          );
        }
      }

      /** @type {HTMLElement|null} */
      const liveRegion = this.querySelector('[ref="liveRegion"]');
      if (liveRegion) {
        liveRegion.textContent = responseData.inList ? "Added" : "Removed";
        setTimeout(() => {
          liveRegion.textContent = "";
        }, 5000);
      }
    } catch (error) {
      if (document.visibilityState === "hidden") return;
      window.NnFavoritesState?.toggle(listType, productId, !newState);
      this.#updateUI();
      card?.classList.remove("nn-list-card--removing");
      console.error("[NnListToggle] Error toggling product in list:", error);
    }
  }

  /**
   * The grid card to collapse when this product is removed, but only on the
   * list's own collection page. Prefers the nearest grid `<li>`, falling back
   * to a product-card wrapper for themes that use a div-based grid.
   * @returns {HTMLElement|null}
   */
  #listPageCard() {
    if (this.dataset.onListPage !== "true") return null;
    return /** @type {HTMLElement|null} */ (
      this.closest("li") ||
        this.closest(
          ".card-wrapper, product-card, .product-card, [class*='product-card'], .grid__item",
        )
    );
  }

  /** @param {AbortSignal} signal */
  #subscribeToState(signal) {
    this.#updateUI();
    window.NnFavoritesState.addEventListener(
      "change",
      (/** @type {CustomEvent} */ e) => {
        if (e.detail?.listType === this.dataset.listType && !this.#isToggling)
          this.#updateUI();
      },
      { signal },
    );
  }

  #updateUI() {
    /** @type {HTMLButtonElement|null} */
    const toggleButton = this.querySelector('[ref="toggleButton"]');
    toggleButton?.setAttribute("aria-pressed", this.#inList.toString());
    toggleButton?.setAttribute(
      "aria-label",
      this.#inList ? this.dataset.removeLabel : this.dataset.addLabel,
    );

    /** @type {HTMLElement|null} */
    const textContent = this.querySelector('[ref="textContent"]');
    if (textContent)
      textContent.textContent = this.#inList
        ? this.dataset.removeLabel
        : this.dataset.addLabel;
  }
}

if (!customElements.get("nn-list-toggle-component")) {
  customElements.define("nn-list-toggle-component", NnListToggleComponent);
}

/**
 * List types already seeded this page load, so the seed runs once per list.
 * @type {Set<string>}
 */
const seededOwnLists = new Set();

/**
 * On a list's own collection page the server renders the full membership, but
 * nothing writes it into NnFavoritesState unless a header icon happens to be on
 * the page to seed it. Without that base seed the stored toggle log is only a
 * partial delta, so `hasList` is true while `getProducts` is incomplete — every
 * product not in the partial log then renders as "not in list" (empty star),
 * and clicking it does an idempotent add instead of a remove.
 *
 * Seed the authoritative membership straight from the in-list toggles the server
 * rendered on this page. Stamped with the current time because this render is
 * the freshest known snapshot, so it supersedes any stale partial log in storage
 * (setProducts keeps any newer local mutations on top).
 * @param {string} [listType]
 */
function seedOwnListPage(listType) {
  const state = window.NnFavoritesState;
  if (!state || !listType || seededOwnLists.has(listType)) return;

  const onPage = Array.from(
    document.querySelectorAll(
      `nn-list-toggle-component[data-on-list-page="true"][data-list-type="${CSS.escape(listType)}"]`,
    ),
  );
  if (!onPage.length) return;

  seededOwnLists.add(listType);

  const ids = Array.from(
    new Set(
      onPage
        .filter((el) => el.dataset.inList === "true")
        .map((el) => el.dataset.productId)
        .filter(Boolean),
    ),
  );

  const handle =
    state.getCollectionHandle(listType) ||
    location.pathname.split("/collections/")[1]?.split(/[/?#]/)[0];

  state.setProducts(
    listType,
    { handle, updated_at: new Date().toISOString() },
    ids,
  );
}

/** Seeds every list whose own collection page we're currently on. */
function seedOwnListPages() {
  const onPage = Array.from(
    document.querySelectorAll(
      'nn-list-toggle-component[data-on-list-page="true"][data-logged-in="true"]',
    ),
  );
  for (const listType of new Set(onPage.map((el) => el.dataset.listType))) {
    seedOwnListPage(listType);
  }
}

// Run once the DOM is parsed (so all toggles are present) and state is ready.
function initOwnListSeed() {
  const run = () => {
    if (window.NnFavoritesState) {
      seedOwnListPages();
    } else {
      document.addEventListener("nn-favorites:ready", seedOwnListPages, {
        once: true,
      });
    }
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run, { once: true });
  } else {
    run();
  }
}

initOwnListSeed();

})();
